import { forwardRef, useCallback, useEffect, useState } from 'react';

export interface IUptimeCurrencyInputProps {
    value: number | string;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    onChange: (value: number) => void;
}

const formatCurrencyDisplay = (value: string): string => {
    const numericValue = value.replace(/\D/g, '');

    if (!numericValue) {
        return '';
    }

    const cents = parseInt(numericValue, 10);
    const reais = Math.floor(cents / 100);
    const centavos = cents % 100;

    return `R$ ${reais.toLocaleString('pt-BR')},${String(centavos).padStart(2, '0')}`;
};

const parseToNumber = (value: string | number): number => {
    if (typeof value === 'number') {
        return value;
    }

    if (!value) {
        return 0;
    }

    const cleanValue = value.toString().replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');

    if (parts.length === 1) {
        return Number.parseFloat(parts[0] || '0');
    }

    const reais = Number.parseFloat(parts[0] || '0');
    const centavos = Number.parseFloat((parts[1] || '00').padEnd(2, '0').substring(0, 2)) / 100;

    return reais + centavos;
};

export const UptimeCurrencyInput = forwardRef<HTMLInputElement, IUptimeCurrencyInputProps>(
    ({ value, className, placeholder, disabled, onChange }, ref) => {
        const [displayValue, setDisplayValue] = useState<string>('');
        const [rawValue, setRawValue] = useState<number>(0);

        useEffect(() => {
            if (value !== undefined && value !== null && value !== '' && value !== 0) {
                const cents = Math.round(parseToNumber(value) * 100);
                setRawValue(cents);
                setDisplayValue(formatCurrencyDisplay(String(cents)));
            } else {
                setRawValue(0);
                setDisplayValue('');
            }
        }, [value]);

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const numericValue = e.target.value.replace(/\D/g, '');

                if (!numericValue) {
                    setRawValue(0);
                    setDisplayValue('');
                    onChange(0);
                    return;
                }

                const cents = parseInt(numericValue, 10);
                setRawValue(cents);
                setDisplayValue(formatCurrencyDisplay(String(cents)));
                onChange(cents / 100);
            },
            [onChange]
        );

        const handleBlur = useCallback(() => {
            if (rawValue === 0) {
                setDisplayValue('');
                onChange(0);
            } else {
                setDisplayValue(formatCurrencyDisplay(String(rawValue)));
                onChange(rawValue / 100);
            }
        }, [rawValue, onChange]);

        const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
            e.target.select();
        }, []);

        return (
            <input
                ref={ref}
                type='text'
                className={`form-control text-right ${className || ''}`}
                value={displayValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                placeholder={placeholder || 'R$ 00,00'}
                disabled={disabled}
            />
        );
    }
);

UptimeCurrencyInput.displayName = 'UptimeCurrencyInput';
